function isDev() {
    // @ts-expect-error
    return process.env.NODE_ENV !== 'production';
}
/**
 * Specifies the available logging levels.
 */
export const logLevel = {
    none: 0,
    critical: 5,
    error: 10,
    warn: 20,
    info: 30,
    debug: 40
};
let loggers = {};
let appenders = [];
let globalDefaultLevel = isDev() ? logLevel.debug : logLevel.error;
const standardLevels = ['none', 'error', 'warn', 'info', 'debug'];
function isStandardLevel(level) {
    return standardLevels.filter((l) => l === level).length > 0;
}
function appendArgs() {
    return [this, ...Array.from(arguments)];
}
function logFactory(level) {
    const threshold = logLevel[level];
    return function () {
        // In this function, this === logger
        if (this.level < threshold) {
            return;
        }
        // We don't want to disable optimizations (such as inlining) in this function
        // so we do the arguments manipulation in another function.
        // Note that Function#apply is very special for V8.
        const args = appendArgs.apply(this, arguments);
        let i = appenders.length;
        while (i--) {
            appenders[i][level](...args);
        }
    };
}
function logFactoryCustom(level) {
    //This function is the same as logFactory() except that it checks that the method
    //is defined on the appender.
    const threshold = logLevel[level];
    return function () {
        // In this function, this === logger
        if (this.level < threshold) {
            return;
        }
        // We don't want to disable optimizations (such as inlining) in this function
        // so we do the arguments manipulation in another function.
        // Note that Function#apply is very special for V8.
        const args = appendArgs.apply(this, arguments);
        let i = appenders.length;
        while (i--) {
            const appender = appenders[i];
            if (appender[level] !== undefined) {
                appender[level](...args);
            }
        }
    };
}
function connectLoggers() {
    let proto = Logger.prototype;
    for (let level in logLevel) {
        if (isStandardLevel(level)) {
            if (level !== 'none') {
                proto[level] = logFactory(level);
            }
        }
        else {
            proto[level] = logFactoryCustom(level);
        }
    }
}
function disconnectLoggers() {
    let proto = Logger.prototype;
    for (let level in logLevel) {
        if (level !== 'none') {
            proto[level] = function () { };
        }
    }
}
/**
 * Gets the instance of a logger associated with a particular id (or creates one if it doesn't already exist).
 *
 * @param id The id of the logger you wish to get an instance of.
 * @return The instance of the logger, or creates a new logger if none exists for that id.
 */
export function getLogger(id) {
    return loggers[id] || new Logger(id);
}
/**
 * Adds an appender capable of processing logs and channeling them to an output.
 *
 * @param appender An appender instance to begin processing logs with.
 */
export function addAppender(appender) {
    if (appenders.push(appender) === 1) {
        connectLoggers();
    }
}
/**
 * Removes an appender.
 * @param appender An appender that has been added previously.
 */
export function removeAppender(appender) {
    appenders = appenders.filter((a) => a !== appender);
}
/**
 * Gets an array of all appenders.
 */
export function getAppenders() {
    return [...appenders];
}
/**
 * Removes all appenders.
 */
export function clearAppenders() {
    appenders = [];
    disconnectLoggers();
}
/**
 * Adds a custom log level that will be added as an additional method to Logger.
 * Logger will call the corresponding method on any appenders that support it.
 *
 * @param name The name for the new log level.
 * @param value The numeric severity value for the level (higher is more severe).
 */
export function addCustomLevel(name, value) {
    if (logLevel[name] !== undefined) {
        throw Error(`Log level "${name}" already exists.`);
    }
    if (isNaN(value)) {
        throw Error('Value must be a number.');
    }
    logLevel[name] = value;
    if (appenders.length > 0) {
        //Reinitialize the Logger prototype with the new method.
        connectLoggers();
    }
    else {
        //Add the custom level as a noop by default.
        Logger.prototype[name] = function () { };
    }
}
/**
 * Removes a custom log level.
 * @param name The name of a custom log level that has been added previously.
 */
export function removeCustomLevel(name) {
    if (logLevel[name] === undefined) {
        return;
    }
    if (isStandardLevel(name)) {
        throw Error(`Built-in log level "${name}" cannot be removed.`);
    }
    delete logLevel[name];
    delete Logger.prototype[name];
}
/**
 * Sets the level of logging for ALL the application loggers.
 *
 * @param level Matches a value of logLevel specifying the level of logging.
 */
export function setLevel(level) {
    globalDefaultLevel = level;
    for (let key in loggers) {
        loggers[key].setLevel(level);
    }
}
/**
 * Gets the level of logging of ALL the application loggers.
 *
 * @return The logLevel value used in all loggers.
 */
export function getLevel() {
    return globalDefaultLevel;
}
/**
 * A logger logs messages to a set of appenders, depending on the log level that is set.
 */
export class Logger {
    /**
     * You cannot instantiate the logger directly - you must use the getLogger method instead.
     */
    constructor(id) {
        /**
         * The id that the logger was created with.
         */
        Object.defineProperty(this, "id", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * The logging severity level for this logger
         */
        Object.defineProperty(this, "level", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        let cached = loggers[id];
        if (cached) {
            return cached;
        }
        loggers[id] = this;
        this.id = id;
        this.level = globalDefaultLevel;
    }
    /**
     * Logs a debug message.
     *
     * @param message The message to log.
     * @param rest The data to log.
     */
    debug(message, ...rest) { }
    /**
     * Logs info.
     *
     * @param message The message to log.
     * @param rest The data to log.
     */
    info(message, ...rest) { }
    /**
     * Logs a warning.
     *
     * @param message The message to log.
     * @param rest The data to log.
     */
    warn(message, ...rest) { }
    /**
     * Logs an error.
     *
     * @param message The message to log.
     * @param rest The data to log.
     */
    error(message, ...rest) { }
    /**
     * Logs a critical error.
     *
     * @param message The message to log.
     * @param rest The data to log.
     */
    critical(message, ...rest) { }
    /**
     * Sets the level of logging for this logger instance
     *
     * @param level Matches a value of logLevel specifying the level of logging.
     */
    setLevel(level) {
        this.level = level;
    }
    /**
     * Returns if the logger is in debug mode or not.
     */
    isDebugEnabled() {
        return this.level === logLevel.debug;
    }
}
//# sourceMappingURL=index.js.map